import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { Download, FileText, Loader2, RefreshCw } from 'lucide-react';
import axiosConfig from '../../../../../../redux/Project/axiosConfig';
import customNotification from '../../../../../../components/ui/custom-notification';

export interface ManualWeldDataFileRow {
  fileName: string;
  s3Key: string;
  docId?: number | null;
  weldId?: string | null;
  uploadDate?: string | null;
  uploadedBy?: string | null;
  size?: number | null;
  source?: string;
}

interface ManualWeldDataFilesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  /** When set, only files for this weld under project/manual_weld_data/{weldId}/. */
  weldId?: string | null;
  assemblyLabel?: string;
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function formatSize(bytes?: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const ManualWeldDataFilesModal: React.FC<ManualWeldDataFilesModalProps> = ({
  isOpen,
  onClose,
  projectId,
  weldId,
  assemblyLabel,
}) => {
  const [files, setFiles] = useState<ManualWeldDataFileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const loadFiles = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const trimmedWeld = String(weldId || '').trim();
      const body: Record<string, unknown> = { projectId };
      if (trimmedWeld) body.weldId = trimmedWeld;

      const res = await axiosConfig.post('pipeDocuments/listManualWeldDataFiles', body);
      const list = res?.data?.data?.files ?? res?.data?.files ?? [];
      setFiles(Array.isArray(list) ? list : []);
    } catch (err: any) {
      setFiles([]);
      customNotification.error(
        err?.response?.data?.message || err?.message || 'Failed to load uploaded weld TXT files'
      );
    } finally {
      setLoading(false);
    }
  }, [projectId, weldId]);

  useEffect(() => {
    if (isOpen) {
      void loadFiles();
    } else {
      setFiles([]);
      setDownloadingKey(null);
    }
  }, [isOpen, loadFiles]);

  const handleDownload = async (file: ManualWeldDataFileRow) => {
    setDownloadingKey(file.s3Key);
    try {
      const res = await axiosConfig.post('pipeDocuments/getManualWeldDataDownloadUrl', {
        s3Key: file.s3Key,
        docId: file.docId || undefined,
        fileName: file.fileName,
      });
      const downloadUrl = res?.data?.data?.downloadUrl ?? res?.data?.downloadUrl;
      if (!downloadUrl) {
        throw new Error('No download URL returned');
      }
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = file.fileName || 'download.txt';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err: any) {
      customNotification.error(
        err?.response?.data?.message || err?.message || `Failed to download ${file.fileName}`
      );
    } finally {
      setDownloadingKey(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[90vw] max-w-[720px] pr-14">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3 pr-2">
            <DialogTitle className="flex items-center gap-2 text-[18px] font-semibold text-blueprint-blue leading-tight">
              <FileText className="h-4 w-4 text-heritage-grey" />
              Uploaded Weld TXT Files
              {!loading ? ` (${files.length})` : ''}
            </DialogTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void loadFiles()}
              disabled={loading}
              className="h-8 border-[#E2E5EA] text-text-grey"
              title="Refresh"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
          {assemblyLabel ? (
            <p className="text-[12px] text-heritage-grey mt-1">{assemblyLabel}</p>
          ) : null}
        </DialogHeader>

        <div className="py-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-heritage-grey">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading files from S3…
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-8 text-[13px] text-heritage-grey">
              {weldId && String(weldId).trim()
                ? `No uploaded weld TXT files found for weld ${String(weldId).trim()}.`
                : 'No manual weld TXT files found for this project.'}
            </div>
          ) : (
            <div className="space-y-2">
              {files.map((file) => (
                <div
                  key={file.s3Key}
                  className="flex items-center justify-between p-3 bg-page-bg border border-[#E2E5EA] rounded-lg hover:bg-white transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <FileText className="h-5 w-5 text-heritage-grey flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-text-grey truncate">
                        {file.fileName}
                      </p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-heritage-grey mt-0.5">
                        {file.weldId ? <span>Weld: {file.weldId}</span> : null}
                        <span>{formatDate(file.uploadDate)}</span>
                        {formatSize(file.size) ? <span>{formatSize(file.size)}</span> : null}
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void handleDownload(file)}
                    disabled={downloadingKey === file.s3Key}
                    className="h-8 w-8 p-0 border-[#E2E5EA] text-text-grey hover:bg-white flex-shrink-0"
                    title="Download"
                  >
                    {downloadingKey === file.s3Key ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
